import InternalChatAPI from 'dashboard/api/internalChat';
import {
  sortByCreatedAt,
  upsertMessageById,
} from './internalChatMessageHelpers';
import {
  applyWindowEvents,
  inMessageWindow,
  mergeWindowMeta,
  revalidateMessageWindow,
} from './internalChatMessageWindow';

const requestOwner = (state, channelId) => {
  const generation = state.contextGeneration;
  const selection = state.channelSelectionGeneration;
  const request = state.messageRequestGeneration[channelId];
  return () =>
    generation === state.contextGeneration &&
    selection === state.channelSelectionGeneration &&
    request === state.messageRequestGeneration[channelId] &&
    state.channels.some(channel => channel.id === channelId);
};

const finishRequest = async (
  { state, commit, dispatch },
  current,
  channelId
) => {
  if (!current()) return;
  commit('END_MESSAGE_REQUEST', channelId);
  commit('SET_UI_FLAG', { isFetchingMessages: false });
  if (!state.messageRefreshQueued[channelId]) return;
  commit('QUEUE_MESSAGE_REFRESH', { channelId, queued: false });
  try {
    await dispatch('refreshMessages', channelId);
  } catch {
    // The refresh records its error without replacing the completed request result.
  }
};

export const historyActions = {
  async fetchMessages(context, channelOrOptions) {
    const { commit, state } = context;
    const options =
      typeof channelOrOptions === 'object'
        ? channelOrOptions
        : { channelId: channelOrOptions };
    const { channelId, params = {}, append = false, newer = false } = options;
    const before = new Map(
      (state.messages[channelId] || []).map(message => [message.id, message])
    );
    commit('START_MESSAGE_REQUEST', channelId);
    const current = requestOwner(state, channelId);
    commit('SET_UI_FLAG', { isFetchingMessages: true });
    commit('SET_ERROR', { messages: null });
    try {
      const response = await InternalChatAPI.getMessages(channelId, params);
      if (!current()) return [];
      const meta = append
        ? mergeWindowMeta(
            state.messageMeta[channelId] || {},
            response.data.meta,
            newer
          )
        : response.data.meta;
      const base = (
        append
          ? response.data.messages.reduce(
              upsertMessageById,
              state.messages[channelId] || []
            )
          : response.data.messages
      )
        .slice()
        .sort(sortByCreatedAt);
      const realtime = (state.messages[channelId] || []).filter(
        message =>
          before.get(message.id) !== message &&
          inMessageWindow(message, base, meta)
      );
      const messages = applyWindowEvents(
        realtime.reduce(upsertMessageById, base),
        state.messageEvents[channelId] || [],
        meta
      );
      commit(append ? 'MERGE_MESSAGES' : 'SET_MESSAGES', {
        channelId,
        messages,
        meta: response.data.meta,
        newer,
      });
      return messages;
    } catch (error) {
      if (!current()) return [];
      commit('SET_ERROR', { messages: error });
      throw error;
    } finally {
      await finishRequest(context, current, channelId);
    }
  },
  async loadOlderMessages({ dispatch, state }, channelId) {
    const meta = state.messageMeta[channelId] || {};
    if (!meta.has_more || !meta.before) return [];
    return dispatch('fetchMessages', {
      channelId,
      params: { before: meta.before, before_id: meta.before_id },
      append: true,
    });
  },
  async loadNewerMessages({ dispatch, state }, channelId) {
    const meta = state.messageMeta[channelId] || {};
    if (!meta.has_more_after || !meta.after) return [];
    return dispatch('fetchMessages', {
      channelId,
      params: { after: meta.after, after_id: meta.after_id },
      append: true,
      newer: true,
    });
  },
  async refreshMessages(context, channelId) {
    const { state, commit, dispatch } = context;
    if (state.messageEvents[channelId]) {
      commit('QUEUE_MESSAGE_REFRESH', { channelId, queued: true });
      return null;
    }
    const records = (state.messages[channelId] || [])
      .slice()
      .sort(sortByCreatedAt);
    const meta = state.messageMeta[channelId] || {};
    if (!records.length) return dispatch('fetchMessages', channelId);
    commit('START_MESSAGE_REQUEST', channelId);
    const current = requestOwner(state, channelId);
    commit('SET_UI_FLAG', { isFetchingMessages: true });
    commit('SET_ERROR', { messages: null });
    try {
      const page = await revalidateMessageWindow({
        records,
        meta,
        current,
        field: 'messages',
        getPage: async params =>
          (await InternalChatAPI.getMessages(channelId, params)).data,
        getMessage: async id =>
          (await InternalChatAPI.getMessage(channelId, id)).data,
      });
      if (!current() || !page) return null;
      commit('SET_MESSAGES', {
        channelId,
        ...page,
        messages: applyWindowEvents(
          page.messages,
          state.messageEvents[channelId] || [],
          page.meta
        ),
      });
      return page;
    } catch (error) {
      if (current()) commit('SET_ERROR', { messages: error });
      throw error;
    } finally {
      await finishRequest(context, current, channelId);
    }
  },
};
